import { LoadingButton } from '@mui/lab';
import { Divider, Stack, Switch, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';

import { LoadingScreen } from 'components/LoadingScreen';
import { changeDisablePayment, changeDisablePaymentVersion } from 'redux/slices/disablePayment';
import { changeRecurrent } from 'redux/slices/recurrent';
import { editDisablePaymentThunk, getDisablePaymentThunk } from 'redux/thunks/disablePayment';
import { editRecurrentThunk, getRecurrentThunk } from 'redux/thunks/recurrent';
import { RootState, useDispatch, useSelector } from '../../redux/store';
import {
  DISABLE_PAYMENT_PLATFORMS,
  DisablePaymentPlatforms,
  DisablePaymentPlatformsVersion,
} from 'types/disablePayment';
import { isEnum } from './util';

const DisablePayment = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const disablePayment = useSelector((state: RootState) => state.disablePayment.disablePayment);
  const disablePaymentError = useSelector(
    (state: RootState) => state.disablePayment.error?.response?.data?.message
  );

  const recurrent = useSelector((state: RootState) => state.recurrent.recurrent);
  const recurrentError = useSelector(
    (state: RootState) => state.recurrent.error?.response?.data?.message
  );

  const [isLoading, setIsLoading] = useState(false);

  const [isLoaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        await dispatch(getDisablePaymentThunk());
      } catch (error) {
        enqueueSnackbar(`Ошибка загрузки платёжки, ${disablePaymentError}`, {
          variant: 'error',
        });
      }

      try {
        await dispatch(getRecurrentThunk());
      } catch (error) {
        enqueueSnackbar(`Ошибка загрузки рекурентных платежей, ${recurrentError}`, {
          variant: 'error',
        });
      }

      setLoaded(true);
    };

    load();
  }, [disablePaymentError, dispatch, enqueueSnackbar, recurrentError]);

  const changeHandler =
    (platform: DisablePaymentPlatforms) => (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(changeDisablePayment({ platform, value: event.target.checked ? 1 : 0 }));
    };

  const changeHandlerVersion =
    (platform: DisablePaymentPlatformsVersion) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      dispatch(changeDisablePaymentVersion({ platform, value: event.target.value }));
    };

  const handleRecurrentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(changeRecurrent(event.target.checked));
  };

  const handleSave = async () => {
    setIsLoading(true);

    try {
      if (disablePayment) await dispatch(editDisablePaymentThunk(disablePayment));

      enqueueSnackbar('Платёжка обновлена успешно', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(`Платёжка не обновлена, ${disablePaymentError}`, {
        variant: 'error',
      });
    }

    try {
      if (recurrent !== null) await dispatch(editRecurrentThunk({ recurrent }));

      enqueueSnackbar('Рекурентные платежи обновлены успешно', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(`Рекурентные платежи не обновлены, ${recurrentError}`, {
        variant: 'error',
      });
    }

    setIsLoading(false);
  };

  if (!disablePayment) return null;

  return (
    <>
      {isLoaded ? (
        <>
          <Typography variant="h5" component="h1">
            Платёжка
          </Typography>

          <Typography variant="h6">
            ⚠️ Отключение чек-бокса скрытия платёжки применяется ко всей платформе вне зависимости
            от указанных версий.
          </Typography>

          <Stack direction="row" gap={4}>
            <Stack gap={2}>
              {Object.values(DISABLE_PAYMENT_PLATFORMS).map((platform, i) => (
                <Stack key={platform}>
                  {isEnum(platform, DisablePaymentPlatforms) ? (
                    <Stack
                      direction="row"
                      alignItems="center"
                      height="40px"
                      width="500px"
                      justifyContent="space-between"
                    >
                      <Typography
                        component="label"
                        fontSize="1rem"
                        lineHeight="24px"
                        marginLeft="12px"
                        whiteSpace="nowrap"
                      >
                        {platform}
                      </Typography>

                      <Switch
                        onChange={changeHandler(platform)}
                        checked={!!disablePayment.data[platform]}
                      />
                    </Stack>
                  ) : isEnum(platform, DisablePaymentPlatformsVersion) ? (
                    <Stack
                      direction="row"
                      alignItems="center"
                      height="40px"
                      width="500px"
                      justifyContent="space-between"
                    >
                      <Typography
                        component="label"
                        fontSize="1rem"
                        lineHeight="24px"
                        marginLeft="12px"
                        whiteSpace="nowrap"
                      >
                        Ограничить по версии
                      </Typography>

                      <TextField
                        label={platform}
                        variant="outlined"
                        type="text"
                        placeholder="Версия"
                        size="small"
                        onChange={changeHandlerVersion(platform)}
                        value={disablePayment.data[platform]}
                      />
                    </Stack>
                  ) : (
                    <></>
                  )}

                  {i % 2 > 0 && <Divider sx={{ width: '100%', marginTop: '8px' }} />}
                </Stack>
              ))}
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center" width="300px" justifyContent="space-between">
            <Typography
              component="label"
              fontSize="1rem"
              lineHeight="24px"
              marginLeft="12px"
              whiteSpace="nowrap"
            >
              Рекурентные платежи
            </Typography>

            <Switch onChange={handleRecurrentChange} checked={!!recurrent} />
          </Stack>
          <LoadingButton
            loading={!!isLoading}
            variant="contained"
            onClick={handleSave}
            sx={{ textTransform: 'none', width: 'fit-content' }}
          >
            <Typography
              fontSize="0.9375rem"
              fontWeight="500"
              lineHeight="26px"
              letterSpacing="0.46px"
              color="white"
            >
              Сохранить
            </Typography>
          </LoadingButton>
        </>
      ) : (
        <LoadingScreen />
      )}
    </>
  );
};

export default DisablePayment;
